import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calculator, Car, Sparkles, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { CommuteCalculatorContent } from "@/components/dailyCommute/CommuteCalculatorContent";
import { GuestCommuteContent } from "@/components/dailyCommute/GuestCommuteContent";
import { VehicleImagePreview } from "@/components/dailyCommute/VehicleImagePreview";
import { useCommuteCalculator } from "@/hooks/useCommuteCalculator";

export type CommuteCalculatorVariant = "page" | "embedded";

type CommuteRole = "host" | "guest";

function parseCommuteRole(value: string | null): CommuteRole | null {
  if (value === "guest" || value === "host") return value;
  return null;
}
interface CommuteCalculatorShellProps {
  variant?: CommuteCalculatorVariant;
  enabled?: boolean;
  idPrefix?: string;
  className?: string;
}

const ROLE_COPY: Record<CommuteRole, { title: string; subtitle: string }> = {
  host: {
    title: "See how much you can earn by hosting",
    subtitle:
      "Enter your route and vehicle details to get an AI-powered per-seat fare for your daily commute.",
  },
  guest: {
    title: "See how much you can save as a rider",
    subtitle:
      "Enter your route and we'll compare a solo cab and auto fare against Duri's shared per-seat price.",
  },
};

function PageHero({ role }: { role: CommuteRole }) {
  const copy = ROLE_COPY[role];
  return (
    <section className="relative overflow-hidden bg-host-gradient px-4 pb-16 pt-10 sm:pb-20 sm:pt-14">
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-10 left-1/4 h-40 w-40 rounded-full bg-black/5 blur-2xl" />

      <div className="container relative mx-auto max-w-4xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
          <Calculator className="h-3.5 w-3.5" />
          Commute Calculator
        </div>
        <h1 className="mt-4 text-3xl font-extrabold leading-tight text-white sm:text-4xl md:text-5xl">
          {copy.title}
        </h1>
        <p className="mt-3 max-w-2xl text-base text-white/90 sm:text-lg">{copy.subtitle}</p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm">
          <Sparkles className="h-4 w-4" />
          Smart pricing · Vehicle-aware · Instant savings estimate
        </div>
      </div>
    </section>
  );
}

const ROLE_TABS: Array<{ id: CommuteRole; label: string; icon: typeof Users }> = [
  { id: "host", label: "I'm a Host", icon: Car },
  { id: "guest", label: "I'm a Guest", icon: Users },
];

function RoleTabs({
  role,
  onRoleChange,
}: {
  role: CommuteRole;
  onRoleChange: (role: CommuteRole) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Commute role"
      className="mb-6 grid grid-cols-2 gap-1 rounded-xl bg-muted/70 p-1"
    >
      {ROLE_TABS.map((tab) => {
        const isActive = role === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onRoleChange(tab.id)}
            className={cn(
              "flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all",
              isActive
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function HostContent({
  idPrefix,
  isPage,
}: {
  idPrefix: string;
  isPage: boolean;
}) {
  const calculator = useCommuteCalculator({ enabled: true });

  return (
    <>
      {isPage && !calculator.showFareResults && (
        <div className="mb-6 flex items-start justify-between gap-4 border-b border-border/60 pb-5">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-foreground">Enter your trip details</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Fill in your route and vehicle info, then search for a fair per-seat fare.
            </p>
          </div>
          {calculator.selectedMake && calculator.selectedModel && (
            <VehicleImagePreview
              make={calculator.selectedMake.maNa}
              model={calculator.selectedModel.moNa}
              variant="thumbnail"
            />
          )}
        </div>
      )}

      <CommuteCalculatorContent calculator={calculator} idPrefix={idPrefix} />
    </>
  );
}

export function CommuteCalculatorShell({
  variant = "page",
  enabled = true,
  idPrefix = "commute",
  className,
}: CommuteCalculatorShellProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFromUrl = parseCommuteRole(searchParams.get("role"));
  const [role, setRole] = useState<CommuteRole>(roleFromUrl ?? "host");

  useEffect(() => {
    if (roleFromUrl) setRole(roleFromUrl);
  }, [roleFromUrl]);

  const handleRoleChange = (next: CommuteRole) => {
    setRole(next);
    if (variant === "page") {
      setSearchParams({ role: next }, { replace: true });
    }
  };

  const isPage = variant === "page";

  if (!enabled) return null;

  return (
    <div className={cn(isPage ? "bg-muted/30" : className)}>
      {isPage && <PageHero role={role} />}

      <section
        className={cn(
          isPage
            ? "container relative z-10 mx-auto max-w-3xl px-4 pb-12 sm:px-6 sm:pb-16"
            : "px-0 py-0",
          isPage && "-mt-10 sm:-mt-12"
        )}
      >
        <div
          className={cn(
            isPage && "rounded-2xl border border-border/80 bg-card p-5 shadow-lg sm:p-8"
          )}
        >
          <RoleTabs role={role} onRoleChange={handleRoleChange} />

          {/* Each role mounts its own calculator so only the active one runs its
              detection and API queries. */}
          {role === "host" ? (
            <HostContent idPrefix={`${idPrefix}-host`} isPage={isPage} />
          ) : (
            <GuestCommuteContent idPrefix={`${idPrefix}-guest`} />
          )}
        </div>
      </section>
    </div>
  );
}
